// Pieces every format shares: the guarded tap and submit, the answer options (OptionButton from the design system,
// composed and never restyled: it draws the marks, the lock-in, right and wrong, and the room's split), the tile-or-row
// decision, and two small glyphs.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps, type RefObject } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { OptionButton } from '@/components/OptionButton';
import type { Format, Option, Reveal } from '@shared/types';
import { context2d } from '@/clinical/env';
import type { AnswerPayload, AnswerPhase, FormatViewProps } from './types';
import { answerState, answerStateAny, longestWord, sharePct, tfKind, tilesFor, tileWordBudget, TILE_MAX_CHARS } from './layout';

type ButtonProps = ComponentProps<typeof OptionButton>;
const useIso = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** True once this player has an answer in: a pick, a value, picks or an order. */
export function hasAnswer(p: Pick<FormatViewProps, 'myOptionId' | 'myAnswer'>): boolean {
  const a = p.myAnswer;
  return Boolean(p.myOptionId) || Boolean(a && (a.optionId || typeof a.value === 'number' || a.optionIds?.length || a.order?.length));
}
/** The single option or region this player picked, wherever the screen passed it. */
export const pickOf = (p: Pick<FormatViewProps, 'myOptionId' | 'myAnswer'>): string | undefined => p.myOptionId || p.myAnswer?.optionId || undefined;

/**
 * One answer per question: ignored once an answer is in, when the window is closed, after the reveal, and within
 * 400 ms of a previous send (a double tap on a slow phone). `before` runs just before the answer is sent.
 * Returns true when the answer went.
 */
export function useAnswerSubmit(props: FormatViewProps, before?: (answer: AnswerPayload) => void) {
  const { phase, disabled, onAnswer } = props;
  const last = useRef(0);
  const live = phase === 'answering' && !hasAnswer(props) && !disabled && typeof onAnswer === 'function';
  const submit = useCallback((answer: AnswerPayload): boolean => {
    if (!live) return false;
    const now = Date.now();
    if (now - last.current < 400) return false;
    last.current = now;
    before?.(answer);
    onAnswer?.(answer);
    return true;
  }, [live, onAnswer, before]);
  return { submit, live };
}

/** The single-pick form of useAnswerSubmit: a tap sends { optionId }. */
export function useAnswerTap(props: FormatViewProps, before?: (optionId: string) => void) {
  const pre = useMemo(() => (before ? (a: AnswerPayload) => { if (a.optionId) before(a.optionId); } : undefined), [before]);
  const { submit, live } = useAnswerSubmit(props, pre);
  const tap = useCallback((optionId: string) => { submit({ optionId }); }, [submit]);
  return { tap, live };
}

/** When each answer plays its reveal: a wrong pick shakes first, then the right answer pulses. */
export function revealDelay(id: string, reveal: Reveal | undefined, myOptionId?: string): number {
  if (!reveal || id !== reveal.correctId) return 0;
  return myOptionId && myOptionId !== reveal.correctId ? 420 : 80;
}

/** The viewport's width, kept current. */
export function useViewportWidth(): number {
  const read = () => (typeof window === 'undefined' ? 390 : window.innerWidth || 390);
  const [w, setW] = useState(read);
  useEffect(() => {
    const on = () => setW(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

let measureCtx: CanvasRenderingContext2D | null | undefined;
const measurer = () => (measureCtx === undefined ? (measureCtx = context2d(document.createElement('canvas'))) : measureCtx);

/**
 * Whether these options fit as 2 × 2 tiles in this container: every one at most 32 characters, and no word wider
 * than a tile's text box in the font the options really use (so no word ever breaks in the middle). Undefined when
 * it cannot be measured here (no layout or no canvas).
 */
export function measureTileFit(container: HTMLElement, options: ReadonlyArray<Option>): boolean | undefined {
  if (options.some((o) => o.text.trim().length > TILE_MAX_CHARS)) return false;
  const width = container.clientWidth;
  if (!(width > 0)) return undefined;
  const ctx = measurer();
  if (!ctx) return undefined;
  const sample = container.querySelector('.rd-opt') ?? container;
  const cs = getComputedStyle(sample);
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const room = (width - 12) / 2 - 32;
  return options.every((o) => ctx.measureText(longestWord(o.text)).width <= room + 0.5);
}

/** Tiles or rows for a set of options: measured where the browser can, else a safe count of letters. */
export function useTileFit(ref: RefObject<HTMLElement | null>, options: ReadonlyArray<Option>, enabled: boolean): boolean {
  const vw = useViewportWidth();
  const guess = tilesFor(options, tileWordBudget(vw));
  const key = options.map((o) => o.text).join('\u0001');
  const [measured, setMeasured] = useState<{ key: string; fits: boolean }>();
  useIso(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    let alive = true;
    const run = () => {
      if (!alive) return;
      const fits = measureTileFit(el, options);
      if (fits !== undefined) setMeasured((m) => (m && m.key === key && m.fits === fits ? m : { key, fits }));
    };
    run();
    // Measure again when the width changes, when a web font arrives, and when the look (and with it the face) changes.
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(run) : undefined;
    ro?.observe(el);
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(run, () => undefined);
    fonts?.addEventListener?.('loadingdone', run);
    const mo = typeof MutationObserver === 'function' ? new MutationObserver(run) : undefined;
    mo?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-look'] });
    return () => { alive = false; ro?.disconnect(); mo?.disconnect(); fonts?.removeEventListener?.('loadingdone', run); };
    // options are stood for by key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);
  return measured && measured.key === key ? measured.fits : guess;
}

export interface OptionListProps {
  options: ReadonlyArray<Option>;
  format?: Format;
  phase: AnswerPhase;
  myOptionId?: string;
  reveal?: Reveal;
  live: boolean;
  onTap: (optionId: string) => void;
  /** Force a layout; by default tiles when every option fits a tile, else rows (true or false is always tiles). */
  layout?: 'tiles' | 'rows';
  /** Classroom mode: the marks only, huge. */
  marksOnly?: boolean;
  /** When a question has several right answers (hotspot, multi), all of them: each is marked right at the reveal. */
  right?: readonly string[];
  /** Short rows with a smaller mark (see OptionButton). */
  dense?: boolean;
}

export function OptionList({ options, format, phase, myOptionId, reveal, live, onTap, layout, marksOnly, right, dense }: OptionListProps) {
  const reduce = useReducedMotion() ?? false;
  const box = useRef<HTMLDivElement>(null);
  const tf = format === 'tf';
  const fixed = Boolean(marksOnly || tf || layout);
  const fits = useTileFit(box, options, !fixed);
  const tiles = marksOnly || tf || (layout ? layout === 'tiles' : fits);
  const variant: ButtonProps['variant'] = marksOnly ? 'mark' : tiles ? 'tile' : 'row';
  const revealed = phase === 'revealed' && reveal;
  return (
    <div ref={box} lang="en-GB" className={marksOnly ? 'fx-marks' : 'fx-options'} data-layout={marksOnly ? 'marks' : tiles ? 'tiles' : 'rows'} role="group" aria-label={marksOnly ? 'Answer marks' : tf ? 'True or false' : 'Answers'}>
      {options.map((o, i) => {
        const st = right && right.length ? answerStateAny(o.id, phase, myOptionId, reveal, right) : answerState(o.id, phase, myOptionId, reveal);
        const mine = o.id === myOptionId;
        const kind = tf ? tfKind(o, i) : undefined;
        const pct = revealed ? sharePct(reveal, o.id) : undefined;
        return (
          <div key={o.id} className="fx-cell" data-option={o.id} data-state={st} data-mine={mine ? '' : undefined} data-tf={kind}>
            <OptionButton id={o.id} text={o.text} index={i} variant={variant} state={st as ButtonProps['state']} disabled={!live} onSelect={onTap}
              format={tf ? 'tf' : undefined} mark={kind ? (kind === 'true' ? 'check' : 'cross') : undefined}
              share={pct === undefined ? undefined : pct / 100} revealDelayMs={revealDelay(o.id, reveal, myOptionId)} dense={dense} />
            {revealed && mine && <span className="fx-you" data-tone={st}>You</span>}
            {revealed && marksOnly && (
              <motion.span className="fx-mark-pct" style={{ x: '-50%' }} initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.07 }}>{pct}%</motion.span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function TickGlyph({ size = 24, stroke = 3.2 }: { size?: number; stroke?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 12.8l4.6 4.6L19.5 7" />
    </svg>
  );
}

export function CrossGlyph({ size = 24, stroke = 3.2 }: { size?: number; stroke?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </svg>
  );
}
